import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  useTheme,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Person, People, PersonAdd } from "@mui/icons-material";
import {
  MainButton,
  SecondaryButton,
  SelectionButton,
} from "../global-components/MainButton";
import BmAlert from "../global-components/BM_Alert";
import {
  ValidateLegalName,
  ValidateAddress,
  FormatName,
} from "../services/validationService";
import { BookingContext } from "../bookingContext/BookingContex";
import { handleStep2SubmissionService } from "../bookingServices";
import {
  fetchAuthenticatedPatientProfile,
  fetchDependentsList,
} from "../services/profileService";

const FormInput = ({
  label,
  value,
  onChange,
  onBlur,
  type = "text",
  readOnly = false,
  options = [],
  helperText = "",
  error = false,
}) => {
  const theme = useTheme();
  return (
    <TextField
      select={options.length > 0}
      fullWidth
      size="small"
      variant="outlined"
      label={label}
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={readOnly}
      error={error}
      helperText={helperText}
      InputProps={{
        readOnly,
        style: readOnly ? { backgroundColor: theme.palette.grey[100] } : {},
      }}
      SelectProps={{ native: true }}
    >
      {options.length > 0 &&
        options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
    </TextField>
  );
};

// -------------------------------------------------------------------------
const Step2Identity = () => {
  const {
    bookingPerson,
    setBookingPerson,
    setPatient,
    nextStep,
    isAuthenticated,
    setContactDetails,
  } = useContext(BookingContext);

  const [page, setPage] = useState(0);
  const [bookingType, setBookingType] = useState("");
  const [dependents, setDependents] = useState([]);
  const [loadingDependents, setLoadingDependents] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    gender: "",
    address: "",
  });

  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState(null);

  const hasDependents = dependents.length > 0;

  useEffect(() => {
    // console.log("Step2Identity -> bookingPerson seen:", bookingPerson);
  }, [bookingPerson]);

  // --- Prefill guest name from BookingContext ---
  // useEffect(() => {
  //   if (bookingPerson.authStatus === "guest") {
  //     setFormData((prev) => ({
  //       ...prev,
  //       firstName: bookingPerson.name.firstName || prev.firstName,
  //       lastName: bookingPerson.name.lastName || prev.lastName,
  //     }));
  //   }
  // }, [bookingPerson]);

  // -----------------------------------------------------------------------
  // ✅ Fetch dependents (for authenticated users)
  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        try {
          setLoadingDependents(true);
          const data = await fetchDependentsList(bookingPerson.id);
          setDependents(data || []);
        } catch (err) {
          console.error("Failed to load dependents", err);
        } finally {
          setLoadingDependents(false);
        }
      })();
    }
  }, [isAuthenticated, bookingPerson.id]);

  // -----------------------------------------------------------------------
  const handleSelectType = async (type) => {
    setBookingType(type);
    setPage(1);
    setErrorMsg(null);
    setErrors({});

    if (type === "myself" && isAuthenticated) {
      try {
        setLoadingProfile(true);
        const profile = await fetchAuthenticatedPatientProfile();

        setFormData({
          firstName: profile.firstName,
          lastName: profile.lastName,
          birthDate: profile.birthDate,
          gender: profile.gender,
          address: profile.address,
        });

        setBookingPerson((prev) => ({
          ...prev,
          name: {
            ...prev.name,
            firstName: profile.firstName,
            lastName: profile.lastName,
          },
        }));

        // setPatient((prev) => ({
        //   ...prev,
        //   birthDate: profile.birthDate,
        // }));

        // ✅ Sync verified contact details for Step 3
        setContactDetails((prev) => ({
          ...prev,
          verifiedEmail: profile.email || prev.verifiedEmail,
          verifiedPhone: profile.phone || prev.verifiedPhone,
        }));
      } catch (err) {
        console.error("Profile fetch failed:", err);
        setErrorMsg(err.message || "Failed to load profile data.");
      } finally {
        setLoadingProfile(false);
      }
    } else if (type === "myself" && !isAuthenticated) {
      // Guest booking for self → prefill with guest name from step 1
      setFormData((prev) => ({
        ...prev,
        firstName: bookingPerson.name.firstName || "",
        lastName: bookingPerson.name.lastName || "",
        birthDate: "",
        gender: "",
        address: "",
      }));
    } else if (type === "someone_else") {
      // NEW: brand-new person → MUST NOT prefill with guest name
      setFormData({
        firstName: "",
        lastName: "",
        birthDate: "",
        gender: "",
        address: "",
      });
    } else {
      // For guests, prefill name from context instead of clearing
      setFormData((prev) => ({
        ...prev,
        firstName: bookingPerson.name.firstName || "",
        lastName: bookingPerson.name.lastName || "",
        birthDate: "",
        gender: "",
        address: "",
      }));
    }
  };
  // Determine if gender/birthDate are required
  const patientFieldsRequired = bookingType !== "dependent";


  // -----------------------------------------------------------------------
  const handleBack = () => {
    setPage(0);
    setBookingType("");
    setSelectedDependent(null);
    setErrorMsg(null);
    setErrors({});
  };

  // -----------------------------------------------------------------------
  const validateField = (key, value) => {
    let result = true;
    if (key === "firstName" || key === "lastName") {
      result = ValidateLegalName(value);
    } else if (key === "address") {
      result = ValidateAddress(value);
    }
    setErrors((prev) => ({ ...prev, [key]: result === true ? null : result }));
    return result === true;
  };

const validateForm = () => {
  let valid = true;
  const newErrors = {};

  // Always validate names and address (when relevant)
  ["firstName", "lastName", "address"].forEach((k) => {
    const ok = validateField(k, formData[k]);
    if (!ok) valid = false;
  });

  // Patient-level requiredness:
  // If bookingType is 'dependent' and a dependent is selected, we rely on selectedDependent.
  // Otherwise (myself OR someone_else) gender and birthDate are required.
  const patientFieldsRequired = bookingType !== "dependent";

  if (patientFieldsRequired) {
    if (!formData.birthDate) {
      newErrors.birthDate = "Birthdate is required.";
      valid = false;
    }
    if (!formData.gender) {
      newErrors.gender = "Gender is required.";
      valid = false;
    }
  }

  setErrors((prev) => ({ ...prev, ...newErrors }));
  return valid;
};


  // -----------------------------------------------------------------------
  const handleSubmit = () => {
    setErrorMsg(null);

    if (bookingType === "dependent" && !selectedDependent) {
      setErrorMsg("Please select a dependent before proceeding.");
      return;
    }

    if (bookingType !== "dependent" && !validateForm()) {
      setErrorMsg("Please correct the highlighted errors before continuing.");
      return;
    }
    const finalPatient = {
      bookingFor: bookingType,
      firstName:
        bookingType === "dependent"
          ? selectedDependent.name.split(" ")[0]
          : FormatName(formData.firstName),
      lastName:
        bookingType === "dependent"
          ? selectedDependent.name.split(" ").slice(1).join(" ")
          : FormatName(formData.lastName),
      dob:
        bookingType === "dependent"
          ? selectedDependent.dob || selectedDependent.birthDate || ""
          : formData.birthDate,
      gender:
        bookingType === "dependent"
          ? selectedDependent.gender
          : formData.gender,
      address:
        bookingType === "dependent"
          ? selectedDependent.address
          : formData.address,
      // patientId:
      //   bookingType === "dependent"
      //     ? selectedDependent.patientId
      //     : bookingType === "myself"
      //     ? bookingPerson.id
      //     : null,
      patientId:
        bookingType === "dependent"
          ? selectedDependent.patientId
          : bookingType === "myself"
          ? isAuthenticated
            ? bookingPerson.id
            : null
          : null,
      parentId: isAuthenticated ? bookingPerson.id : null,
    };

    const validationError = handleStep2SubmissionService(
      finalPatient,
      bookingPerson.authStatus
    );
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setPatient(finalPatient);
    nextStep();
  };

  // -----------------------------------------------------------------------
  // ✅ UI Renderers
  const renderSelection = () => (
    <Stack spacing={3}>
      <Typography variant="subtitle1" color="info.main">
        Who is this appointment for?
      </Typography>

      {isAuthenticated && !bookingPerson.username && (
        <BmAlert
          severity="info"
          title="Syncing Account"
          message="Loading your profile details, please wait..."
        />
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <SelectionButton
          btnLabel={
            loadingProfile ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
              </>
            ) : (
              `Myself (${
                bookingPerson.username ||
                bookingPerson.name.firstName ||
                "Guest"
              })`
            )
          }
          icon={<Person />}
          onClick={() => handleSelectType("myself")}
          disabled={loadingProfile || loadingDependents}
        />

        <SelectionButton
          btnLabel={
            loadingDependents ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
              </>
            ) : (
              `Dependent (${dependents.length})`
            )
          }
          icon={<People />}
          onClick={() => handleSelectType("dependent")}
          disabled={!isAuthenticated || loadingDependents}
        />

        <SelectionButton
          btnLabel="Someone Else (New)"
          icon={<PersonAdd />}
          onClick={() => handleSelectType("someone_else")}
          disabled={loadingProfile || loadingDependents}
        />
      </Stack>
    </Stack>
  );

  const renderDependentSelection = () => (
    <Box sx={{ mt: 2 }}>
      {loadingDependents && (
        <BmAlert
          severity="info"
          title="Loading Dependents"
          message="Please wait while we load your dependents..."
        />
      )}
      {!loadingDependents && dependents.length === 0 && (
        <Alert severity="info">You have no dependents registered.</Alert>
      )}
      {dependents.length > 0 && (
        <List dense>
          {dependents.map((dep) => (
            <ListItem
              key={dep.patientId}
              button
              selected={selectedDependent?.patientId === dep.patientId}
              onClick={() => setSelectedDependent(dep)}
            >
              <ListItemText
                primary={dep.name}
                secondary={`DOB: ${dep.dob} | Gender: ${dep.gender || "N/A"}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  const renderForm = () => {
    // Determine read-only behavior per field context
    const isBookingForSelf = bookingType === "myself";
    const isLoggedIn = isAuthenticated;

    const nameReadOnly = isBookingForSelf && isLoggedIn; // lock names if self & logged in
    const genderReadOnly = false; // always editable
    const dobReadOnly = false; // always editable
    const addressReadOnly = false; // always editable

    return (
      <Box>
        {loadingProfile && (
          <BmAlert
            severity="info"
            title="Loading Profile"
            message="Please wait while we fetch your profile..."
          />
        )}

        {bookingType === "dependent" && renderDependentSelection()}

        {(bookingType === "myself" || bookingType === "someone_else") && (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
              <FormInput
                label="First Name"
                value={formData.firstName}
                readOnly={nameReadOnly}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, firstName: e.target.value }))
                }
                onBlur={() => validateField("firstName", formData.firstName)}
                error={!!errors.firstName}
                helperText={errors.firstName || "Enter first name."}
              />
              <FormInput
                label="Last Name"
                value={formData.lastName}
                readOnly={nameReadOnly}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, lastName: e.target.value }))
                }
                onBlur={() => validateField("lastName", formData.lastName)}
                error={!!errors.lastName}
                helperText={errors.lastName || "Enter last name."}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
              <FormInput
                label={`Birthdate${patientFieldsRequired ? " *" : ""}`}
                type="date"
                value={formData.birthDate}
                readOnly={dobReadOnly}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, birthDate: e.target.value }))
                }
                error={!!errors.birthDate}
                helperText={
                  errors.birthDate ||
                  (patientFieldsRequired
                    ? "Enter birthdate."
                    : "Birthdate (optional)")
                }
              />
              <FormInput
                label={`Gender${patientFieldsRequired ? " *" : ""}`}
                value={formData.gender}
                readOnly={genderReadOnly}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, gender: e.target.value }))
                }
                options={["", "MALE", "FEMALE", "OTHER"]}
                error={!!errors.gender}
                helperText={
                  errors.gender ||
                  (patientFieldsRequired
                    ? "Select gender."
                    : "Select gender (optional)")
                }
              />
            </Stack>

            <FormInput
              label="Address"
              value={formData.address}
              readOnly={addressReadOnly}
              onChange={(e) =>
                setFormData((p) => ({ ...p, address: e.target.value }))
              }
              onBlur={() => validateField("address", formData.address)}
              error={!!errors.address}
              helperText={errors.address || "Address required."}
            />

            {isBookingForSelf && isLoggedIn && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                You can update your gender, birthdate, or address here if
                they’re missing or outdated.
              </Typography>
            )}
          </>
        )}

        {errorMsg && (
          <BmAlert severity="error" sx={{ mt: 2 }} message={errorMsg} />
        )}

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          justifyContent="space-between"
          mt={3}
          spacing={2}
        >
          <SecondaryButton btnLabel="Back" onClick={handleBack} />
          <MainButton
            btnLabel={loadingProfile ? "Loading..." : "Continue"}
            onClick={handleSubmit}
            loading={loadingProfile}
            disabled={loadingProfile || loadingDependents}
          />
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Divider sx={{ mb: 2 }} />
      {page === 0 ? renderSelection() : renderForm()}
    </Box>
  );
};

export default Step2Identity;
